import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { cancelBdcInvoice } from "../../../services";
import { BdcInvoiceActionResponse } from "../../../types/bdc.type";
import { BDC_CANCEL_REASON_MAX } from "../constants";
import { checkCancelReason } from "./invoiceNo";

/**
 * ยกเลิก INV — the number stays used (marked ยกเลิก, reprintable with the stamp) and the PO goes
 * back to ยืนยัน DO. Refused (409) once the PO is saved as รอนำส่ง 7-11: then it takes a CN.
 */
export default function CancelInvoiceDialog({
  invoiceNo,
  poNumber,
  onClose,
  onCancelled,
}: Readonly<{
  /** null = closed. */
  invoiceNo: string | null;
  poNumber?: string;
  onClose: () => void;
  onCancelled: (result: BdcInvoiceActionResponse | null) => void;
}>) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReason("");
    setTouched(false);
  }, [invoiceNo]);

  const error = checkCancelReason(reason);

  const submit = async () => {
    setTouched(true);
    if (!invoiceNo || error) return;
    setSaving(true);
    try {
      const {
        data: { data },
      } = await cancelBdcInvoice(invoiceNo, reason.trim());
      onCancelled(data);
    } catch {
      // the interceptor shows the server's reason (409: already cancelled / รอนำส่ง 7-11)
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={invoiceNo !== null} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>ยกเลิก INV {invoiceNo}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          เลข {invoiceNo} จะถูกบันทึกเป็น "ยกเลิก" และใช้ซ้ำไม่ได้ · PO {poNumber ?? ""} กลับไปเป็น ยืนยัน DO
          แล้วออก INV ใหม่ได้ (ได้เลขใหม่)
        </Alert>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="เหตุผลที่ยกเลิก"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && Boolean(error)}
          helperText={touched && error ? error : `${reason.trim().length}/${BDC_CANCEL_REASON_MAX}`}
          slotProps={{ htmlInput: { maxLength: BDC_CANCEL_REASON_MAX + 50 } }}
        />
        <Typography variant="caption" color="text.secondary">
          เหตุผลแสดงในทะเบียน INV และบนใบที่พิมพ์ซ้ำ
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          ไม่ยกเลิก
        </Button>
        <Button color="error" variant="contained" onClick={submit} loading={saving} disabled={touched && Boolean(error)}>
          ยืนยันยกเลิก INV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
